import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Scale, ShieldCheck, AlertCircle } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const error = location.state?.error;

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0F4C81] text-white p-14">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded bg-white/15 flex items-center justify-center">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg tracking-tight">makrolog</div>
            <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">Rechts-OCR Plattform</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">
            Verkündungsblätter automatisiert erfassen.
          </h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">
            Crawler für alle Länder-Gesetz- und Verordnungsblätter, KI-gestützte OCR-Analyse
            und ein Editor für die strukturierte Gliederung — PDF und XML als Ergebnis.
          </p>
        </div>
        <div className="text-xs text-white/50">© {new Date().getFullYear()} Makrolog · Interne Anwendung</div>
      </div>

      {/* Right: login */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded bg-[#0F4C81] flex items-center justify-center">
              <Scale className="h-4.5 w-4.5 text-white" />
            </div>
            <span className="font-heading font-bold text-gray-900">makrolog Rechts-OCR</span>
          </div>

          <h2 className="font-heading text-2xl font-bold text-gray-900 tracking-tight">Anmelden</h2>
          <p className="mt-2 text-sm text-gray-500">Bitte melden Sie sich mit Ihrem Makrolog-Google-Konto an.</p>

          {error && (
            <div
              className="mt-6 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
              data-testid="login-error"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={login}
            data-testid="google-login-btn"
            className="mt-6 w-full bg-[#0F4C81] hover:bg-[#0d3f6b] text-white h-11"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#fff" d="M12 11v2.8h4.5c-.2 1.2-1.4 3.5-4.5 3.5-2.7 0-4.9-2.2-4.9-5s2.2-5 4.9-5c1.5 0 2.6.6 3.2 1.2l2.2-2.1C16.6 3.1 14.5 2.2 12 2.2 6.9 2.2 2.8 6.3 2.8 11.4S6.9 20.6 12 20.6c4.9 0 8.2-3.4 8.2-8.3 0-.6-.1-1-.1-1.3H12z"/>
            </svg>
            Mit Google anmelden
          </Button>

          <div className="mt-5 flex items-center gap-2 rounded-md bg-gray-50 border border-gray-200 p-3">
            <ShieldCheck className="h-4 w-4 text-[#0F4C81] shrink-0" />
            <p className="text-xs text-gray-600">
              Zugriff ausschließlich für Konten mit der Endung <span className="font-mono font-semibold text-gray-900">@makrolog.de</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
